/**
 * Override Scope Resolution
 *
 * ============================================================================
 * SCOPE HIERARCHY
 * ============================================================================
 *
 * Vibesites supports five scoping levels for overrides. Scopes are encoded
 * as key prefixes in the baked override maps (sectionOverrides,
 * contentOverrides, designOverrides, slotGroupOverrides,
 * collectionBindingOverrides, styleOverrides).
 *
 *   KEY FORMAT                              MATCHES
 *   ──────────────────────────────────────  ──────────────────────────────────
 *   _global                                Every page on the site.
 *
 *   _template:{templateGroup}              All pages whose pages.json entry
 *                                          has `template === templateGroup`.
 *                                          e.g. _template:property matches
 *                                          property-home, property-floorplans,
 *                                          property-amenities, etc. across
 *                                          ALL properties.
 *
 *   _template:{baseAlias}                  All expanded instances of a specific
 *                                          template page. Matched by checking
 *                                          whether the expanded alias starts
 *                                          with `baseAlias--`.
 *                                          e.g. _template:property-home matches
 *                                          property-home--ok--tulsa--barrington
 *                                          but NOT property-floorplans--ok--...
 *
 *   _path:{urlPrefix}                      All pages whose URL path starts
 *                                          with the prefix. Slash-boundary
 *                                          matching: _path:/foo matches /foo
 *                                          and /foo/bar but NOT /foobar.
 *                                          e.g. _path:/apartments/ok/tulsa/barrington
 *                                          matches that property's home, floor
 *                                          plans, amenities, etc.
 *
 *   {exactAlias}                           One specific page (by expanded alias).
 *                                          e.g. property-home--ok--tulsa--barrington
 *
 * ============================================================================
 * RESOLUTION ORDER
 * ============================================================================
 *
 * For SECTION OVERRIDES (which blocks appear on a page):
 *   All matching scopes are CONCATENATED — blocks from every matching scope
 *   level appear together. Order: global → template-group → template-alias
 *   → path → page.
 *
 * For CONTENT/DESIGN/SLOT-GROUP/COLLECTION-BINDING/STYLE OVERRIDES (values):
 *   The MOST SPECIFIC matching scope wins for each token path.
 *   Priority: page > path > template-alias > template-group > global.
 *
 * ============================================================================
 * TEMPLATE vs PATH DISAMBIGUATION
 * ============================================================================
 *
 * _template: keys are resolved against the page's `template` field and base
 * alias (from pages.json). They match consistently across all properties
 * because they operate on the template structure, not the URL.
 *
 * _path: keys are resolved against the page's URL path. They match a specific
 * URL subtree, which for corporate sites means a specific property's pages.
 *
 * Use _template: when you want "all property home pages" or "all property pages".
 * Use _path: when you want "one specific property's pages".
 *
 * ============================================================================
 * EXAMPLES
 * ============================================================================
 *
 * sectionOverrides = {
 *   "_global":                              [...],  // GTM, CallRail on every page
 *   "_template:property":                   [...],  // ResidentBoost on all property pages
 *   "_template:property-home":              [...],  // Hero banner on all property home pages
 *   "_path:/apartments/ok/tulsa/barrington":[...],  // Custom block on Barrington only
 *   "property-home--ok--tulsa--barrington": [...],  // Block on Barrington's home page only
 * }
 *
 * contentOverrides = {
 *   "gtm.container_id":                     { ... },  // global GTM ID
 *   "_template:property::residentboost.apartment_id": { overrideType: 'FIELD_MAPPING', mappedField: 'custom_residentboost_apartment_id' },
 *   "_path:/apartments/ok/tulsa/barrington::residentboost.apartment_id": { overrideType: 'VALUE', customValue: 'override-id' },
 * }
 */

import type { SectionOverrideRecord, SectionOverridesMap } from './section-merge';

// ============================================================================
// Types
// ============================================================================

export type ScopeLevel = 'global' | 'template-group' | 'template-alias' | 'path' | 'page';

/**
 * Context about the current page needed for scope resolution.
 *
 * For non-template pages, `template` and `baseAlias` will be undefined.
 * For template-expanded pages (corporate sites), all fields are present.
 */
export interface ScopePageContext {
  /** Expanded page alias (e.g. "property-home--ok--tulsa--barrington") */
  alias: string;
  /** URL path (e.g. "/apartments/ok/tulsa/barrington") */
  path: string;
  /** Template group from pages.json (e.g. "property"), undefined for static pages */
  template?: string;
  /** Base alias before template expansion (e.g. "property-home"), undefined for static pages */
  baseAlias?: string;
}

// ============================================================================
// Key Parsing Helpers
// ============================================================================

const GLOBAL_KEY = '_global';
const TEMPLATE_PREFIX = '_template:';
const PATH_PREFIX = '_path:';

/**
 * Classify a scope key into its scope level.
 */
export function classifyScopeKey(key: string): ScopeLevel {
  if (key === GLOBAL_KEY) return 'global';
  if (key.startsWith(TEMPLATE_PREFIX)) {
    const value = key.slice(TEMPLATE_PREFIX.length);
    // A template-group key matches a template field value (no dashes with --).
    // A template-alias key is a base page alias like "property-home".
    // Heuristic: if the value contains a dash (e.g. "property-home"), it's a
    // template-alias. Plain values like "property", "state", "city" are groups.
    // This heuristic works because template groups are single words or don't
    // contain hyphens, while page aliases always do.
    if (value.includes('-')) return 'template-alias';
    return 'template-group';
  }
  if (key.startsWith(PATH_PREFIX)) return 'path';
  return 'page';
}

/**
 * Check whether a _path: key matches a given page path.
 * Uses slash-boundary matching: _path:/foo matches /foo and /foo/bar
 * but NOT /foobar.
 */
function pathKeyMatches(key: string, pagePath: string): boolean {
  const prefix = key.slice(PATH_PREFIX.length).replace(/\/+$/, '');
  const normalized = pagePath.replace(/\/+$/, '');
  return normalized === prefix || normalized.startsWith(prefix + '/');
}

/**
 * Check whether a _template: key matches the given page context.
 */
function templateKeyMatches(key: string, ctx: ScopePageContext): boolean {
  const value = key.slice(TEMPLATE_PREFIX.length);
  const level = classifyScopeKey(key);

  if (level === 'template-group') {
    return !!ctx.template && ctx.template === value;
  }

  // template-alias: check if the expanded alias starts with the base alias
  if (ctx.baseAlias) {
    return ctx.baseAlias === value;
  }
  // Fallback: check if alias starts with value--
  return ctx.alias === value || ctx.alias.startsWith(value + '--');
}

// ============================================================================
// Section Override Resolution (concatenating)
// ============================================================================

/**
 * Resolve section overrides for a page by collecting from all matching scopes.
 *
 * All matching scope levels are concatenated so that blocks from global,
 * template, path, and page scopes all appear together. The order follows the
 * hierarchy: global first, page-specific last.
 *
 * @param overridesMap - The full section overrides map (keyed by scope)
 * @param ctx - Current page context
 * @returns Combined array of section override records from all matching scopes
 */
export function resolveSectionOverrides(
  overridesMap: SectionOverridesMap,
  ctx: ScopePageContext,
): SectionOverrideRecord[] {
  const global = overridesMap[GLOBAL_KEY] || [];

  const templateGroupOverrides: SectionOverrideRecord[] = [];
  const templateAliasOverrides: SectionOverrideRecord[] = [];
  const pathOverrides: SectionOverrideRecord[] = [];

  for (const [key, records] of Object.entries(overridesMap)) {
    if (key === GLOBAL_KEY) continue;
    if (!key.startsWith('_')) continue;

    if (key.startsWith(TEMPLATE_PREFIX)) {
      if (!templateKeyMatches(key, ctx)) continue;
      const level = classifyScopeKey(key);
      if (level === 'template-group') {
        templateGroupOverrides.push(...records);
      } else {
        templateAliasOverrides.push(...records);
      }
    } else if (key.startsWith(PATH_PREFIX)) {
      if (pathKeyMatches(key, ctx.path)) {
        pathOverrides.push(...records);
      }
    }
  }

  const page = overridesMap[ctx.alias] || [];

  return [
    ...global,
    ...templateGroupOverrides,
    ...templateAliasOverrides,
    ...pathOverrides,
    ...page,
  ];
}

// ============================================================================
// Content Override Resolution (most-specific wins)
// ============================================================================

/**
 * Resolve a scoped override value from a flat override map.
 *
 * Override maps (content, design, slot-group, collection-binding, style) use
 * composite keys: `{scope}::{tokenPath}` for scoped entries, or just
 * `{tokenPath}` for global entries.
 *
 * Returns the value from the most-specific matching scope.
 *
 * @param overrides - The flat override map
 * @param tokenPath - The token path to look up (e.g. "hero.title")
 * @param ctx - Current page context
 * @returns The override value from the most specific matching scope, or undefined
 */
export function resolveScopedOverride<T>(
  overrides: Record<string, T> | undefined,
  tokenPath: string,
  ctx: ScopePageContext,
): T | undefined {
  if (!overrides) return undefined;

  // Priority 1a: Exact page alias (e.g. "property-home--ok--tulsa--barrington::hero.title")
  const pageKey = `${ctx.alias}::${tokenPath}`;
  if (overrides[pageKey] !== undefined) return overrides[pageKey];

  // Priority 1b: Normalized page path as scope key (legacy format).
  // Existing overrides use the URL path (without leading slash) as page_scope,
  // e.g. "apartments/ok/tulsa/barrington::hero.title"
  const normalizedPath = ctx.path.replace(/^\/+|\/+$/g, '');
  if (normalizedPath && normalizedPath !== ctx.alias) {
    const pathScopeKey = `${normalizedPath}::${tokenPath}`;
    if (overrides[pathScopeKey] !== undefined) return overrides[pathScopeKey];
  }

  // Priority 2: Path scope (longest matching prefix wins)
  let bestPathMatch: T | undefined;
  let bestPathLength = -1;
  for (const key of Object.keys(overrides)) {
    if (!key.startsWith(PATH_PREFIX)) continue;
    const sepIdx = key.indexOf('::');
    if (sepIdx === -1) continue;
    const scopeKey = key.substring(0, sepIdx);
    const tp = key.substring(sepIdx + 2);
    if (tp !== tokenPath) continue;
    if (pathKeyMatches(scopeKey, ctx.path)) {
      const prefixLen = scopeKey.length;
      if (prefixLen > bestPathLength) {
        bestPathLength = prefixLen;
        bestPathMatch = overrides[key];
      }
    }
  }
  if (bestPathMatch !== undefined) return bestPathMatch;

  // Priority 3: Template alias scope
  if (ctx.baseAlias) {
    const templateAliasKey = `${TEMPLATE_PREFIX}${ctx.baseAlias}::${tokenPath}`;
    if (overrides[templateAliasKey] !== undefined) return overrides[templateAliasKey];
  }

  // Priority 4: Template group scope
  if (ctx.template) {
    const templateGroupKey = `${TEMPLATE_PREFIX}${ctx.template}::${tokenPath}`;
    if (overrides[templateGroupKey] !== undefined) return overrides[templateGroupKey];
  }

  // Priority 5: Global (no scope prefix)
  return overrides[tokenPath];
}
